import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { rueckweg } from './sitzung';
import { Button, Callout, Card, Logo, Spinner } from '@elbklar/ui';
import { CircleAlert } from 'lucide-react';
import { TextField } from '@/components/form/Felder';
import { useBranding, resolveAssetUrl } from '@/hooks/useTenantConfig';

export function LoginPage() {
  const navigate = useNavigate();
  const { login, loginWithGoogle, error, isLoading } = useAuth();
  const { data: branding } = useBranding();
  // `?ziel=` setzt der API-Client, wenn die Sitzung wirklich vorbei ist: die
  // Seite, auf der man war. Nach der Anmeldung geht es dorthin zurück.
  const [params] = useSearchParams();
  const ziel = params.get('ziel');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (!email || !password) {
      setLocalError('E-Mail und Passwort fehlen');
      return;
    }

    try {
      await login(email, password);
      navigate(rueckweg(ziel, '/customers'));
    } catch {
      // Error is handled by AuthContext
    }
  };

  const handleGoogleLogin = () => {
    loginWithGoogle(rueckweg(ziel));
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <Card className="w-full max-w-md">
        <div className="mb-8 mt-4 flex flex-col items-center text-center">
          {branding?.logo_light_url ? (
            <img
              src={resolveAssetUrl(branding.logo_light_url)}
              alt={branding.company_name || 'Logo'}
              className="mb-4 h-16 object-contain"
            />
          ) : (
            <Logo variant="horizontal" height={40} className="mb-6" />
          )}
          <h1 className="t-page-title">Anmelden</h1>
          <p className="mt-2 text-muted-foreground">
            {branding?.company_name
              ? `Willkommen bei ${branding.company_name}`
              : 'Anmelden, um fortzufahren'}
          </p>
        </div>

        {(error || localError) && (
          <Callout tone="danger" icon={CircleAlert} title={error || localError} boxed className="mb-6" />
        )}

        <Button
          className="mb-6 w-full"
          onClick={handleGoogleLogin}
          disabled={isLoading}
        >
          <svg className="size-4" viewBox="0 0 24 24" aria-hidden>
            <path
              fill="currentColor"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="currentColor"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="currentColor"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="currentColor"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          Mit Google anmelden
        </Button>

        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border"></div>
          </div>
          <div className="relative flex justify-center t-small">
            <span className="bg-card px-4 text-muted-foreground">oder</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <TextField
            label="E-Mail"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            autoFocus
            disabled={isLoading}
          />
          <TextField
            label="Passwort"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            disabled={isLoading}
          />
          <Button
            type="submit"
            variant="primary"
            className="w-full"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Spinner className="text-current" />
                Anmelden...
              </>
            ) : (
              'Anmelden'
            )}
          </Button>
        </form>
      </Card>
    </div>
  );
}
